type Status<T> = {
    status: 'ready' | 'pending' | 'error';
    promise: Promise<T>;
};

type CacheEntry<T> = { lastAccessed: number } & Status<T>;

export abstract class AsyncCache<T, TParameters> {

    protected readonly _entries: { [key: string]: CacheEntry<T> } = {};

    require = (parameters: TParameters): Status<T> => {

        const key = this.toKey(parameters);
        let entry = this._entries[key];

        if (!entry || entry.status === 'error') {
            const promise = this.load(parameters);
            this._trackStatus(key, promise);
            entry = this._createEntry(promise);
        }

        entry.lastAccessed = Date.now();
        this._entries[key] = entry;

        const { lastAccessed, ...result } = entry;
        return result;
    };

    loadedEntries = (): { [key: string]: Promise<T> } => {

        const result: { [key: string]: Promise<T> } = {};
        const loadedEntries = Object.entries(this._entries)
            .filter(([key, entry]) => entry.status === 'ready');

        for (const [key, entry] of loadedEntries) {
            result[key] = entry.promise;
        }

        return result;
    };

    protected gc = (expireAfterMs: number) => {
        const entries = Object.entries(this._entries);
        for (const [key, entry] of entries) {
            if (Date.now() - entry.lastAccessed > expireAfterMs) {

                if (entry.status === 'ready') {
                    entry.promise.then(this.dispose);
                }

                delete this._entries[key];
            }
        }
    };

    protected abstract load(parameters: TParameters): Promise<T>;

    protected abstract toKey(parameters: TParameters): string;

    protected dispose = (value: T): void => { };

    private _createEntry = (promise: Promise<T>): CacheEntry<T> => ({
        status: 'pending',
        lastAccessed: Date.now(),
        promise: promise
    });

    private _trackStatus = (key: string, promise: Promise<T>) => {
        promise
            .then(() => this._setStatus(key, 'ready'))
            .catch(() => this._setStatus(key, 'error'));
    };

    private _setStatus = (key: string, status: 'ready' | 'pending' | 'error') => {
        if (this._entries[key]) {
            this._entries[key] = { ...this._entries[key], status: status };
        }
    };
}